import {useRef, useState, type ComponentRef} from 'react';
import type {createResource, createMesh} from 'api/generated';
import {toast} from 'react-toastify';

type CreateFileType = typeof createResource | typeof createMesh;

interface IUseCreateFileProps<T extends CreateFileType> {
  createFile: T;
  callback: (file: Awaited<ReturnType<T>>['data']['data']) => void;
  projectUid?: string;
}

const useCreateFile = <T extends CreateFileType>({createFile, callback, projectUid}: IUseCreateFileProps<T>) => {
  const inputRef = useRef<ComponentRef<'input'>>(null);
  const [isPending, setIsPending] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleUpload = async (files: FileList) => {
    const file = files[0];
    if (!file) {
      return;
    }
    setIsPending(true);
    const {data} = await createFile(
      {file, projectUid},
      {
        onUploadProgress: ev => {
          ev.total && setProgress(Math.round((ev.loaded * 100) / ev.total));
        },
      },
    );
    setIsPending(false);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (data.data) {
      toast.success('Upload success');
      callback(data.data);
    } else if (data.errors) {
      toast.error(data.errors[0].detail);
    }
  };

  return {handleUpload, inputRef, isPending, progress};
};

export default useCreateFile;
